import './ExternalStakeholdersSectionCard.scss';
import { ReactElement } from 'react';
import { MdClose, MdSubdirectoryArrowRight } from 'react-icons/md';
import { RiLinkM, RiMore2Fill } from 'react-icons/ri';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useNotImplemented } from '@src/utils/hooks/useNotImplemented';
import { ExternalStakeholder } from '../external-stakeholders-constants';
import { EMPTY_CELL, cellText, listCellText, presentItems } from '../external-stakeholders-utils';
import { useExternalStakeholders } from '../useExternalStakeholders';
import { SectionCard } from './SectionCard';
import { SectionFeedsFooter } from './SectionFeedsFooter';
import { SectionCardBody } from './SectionCardBody';

const EMPTY_STATE_COPY = 'No external stakeholders have been identified for this client yet.';
const ERROR_STATE_COPY = "Couldn't load external stakeholders.";
const DETAIL_HEADING = "Dos, Don'ts & Comms Preferences (per-person detail pane)";
const FEEDS_COPY = 'This content informs Activity Feed and Agendas & Call Prep';

interface ExternalStakeholdersSectionCardProps {
  icon: ReactElement;
  label: string;
}

export const ExternalStakeholdersSectionCard = ({
  icon,
  label
}: ExternalStakeholdersSectionCardProps) => {
  const { data, isLoading, isError } = useExternalStakeholders();
  const notImplemented = useNotImplemented();
  const stakeholders = data?.stakeholders ?? [];

  return (
    <SectionCard
      icon={icon}
      label={label}
      className='external-stakeholders-section-card'
      rightActions={
        <IconButton
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label='More actions'
          onClick={notImplemented}
        >
          <RiMore2Fill />
        </IconButton>
      }
    >
      <div className='external-stakeholders-body'>
        {data && <SourceStrip label={data.sourceLabel} onRemove={notImplemented} />}
        <SectionCardBody
          isLoading={isLoading}
          isError={isError}
          isEmpty={stakeholders.length === 0}
          errorCopy={ERROR_STATE_COPY}
          emptyCopy={EMPTY_STATE_COPY}
        >
          <ExternalStakeholdersTables stakeholders={stakeholders} />
        </SectionCardBody>
      </div>
      {data && (
        <SectionFeedsFooter
          className='external-stakeholders-footer'
          icon={<MdSubdirectoryArrowRight />}
          feedsCopy={FEEDS_COPY}
          updatedAt={data.updatedAt}
        />
      )}
    </SectionCard>
  );
};

interface SourceStripProps {
  label: string;
  onRemove: () => void;
}

const SourceStrip = ({ label, onRemove }: SourceStripProps) => (
  <div className='external-stakeholders-source'>
    <RiLinkM className='external-stakeholders-source-icon' />
    <span className='external-stakeholders-source-label'>{label}</span>
    <IconButton
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      aria-label={`Remove ${label}`}
      onClick={onRemove}
    >
      <MdClose />
    </IconButton>
  </div>
);

interface ExternalStakeholdersProps {
  stakeholders: ExternalStakeholder[];
}

const ExternalStakeholdersTables = ({ stakeholders }: ExternalStakeholdersProps) => (
  <div className='external-stakeholders-tables'>
    <RosterTable stakeholders={stakeholders} />
    <section className='external-stakeholders-detail'>
      <h3 className='external-stakeholders-detail-heading'>{DETAIL_HEADING}</h3>
      <DetailTable stakeholders={stakeholders} />
    </section>
  </div>
);

const RosterTable = ({ stakeholders }: ExternalStakeholdersProps) => (
  <table className='external-stakeholders-table roster'>
    <thead>
      <tr>
        <th className='col-stakeholder'>Stakeholder</th>
        <th className='col-channels'>Channels</th>
        <th className='col-relationship'>Relationship</th>
        <th className='col-authority'>Decision authority</th>
        <th className='col-frequency'>Frequency (Count of interactions)</th>
      </tr>
    </thead>
    <tbody>
      {stakeholders.map(stakeholder => (
        <RosterRow key={stakeholder.email} stakeholder={stakeholder} />
      ))}
    </tbody>
  </table>
);

interface StakeholderRowProps {
  stakeholder: ExternalStakeholder;
}

const RosterRow = ({ stakeholder }: StakeholderRowProps) => {
  const { email, name, pronouns, title, channels, relationship, decisionAuthority, frequency } =
    stakeholder;

  return (
    <tr>
      <td>
        <span className='external-stakeholders-name'>
          {name}
          {pronouns && <span className='external-stakeholders-pronouns'>({pronouns})</span>}
        </span>
        {title && <span className='external-stakeholders-meta'>{title}</span>}
        <span className='external-stakeholders-meta'>{email}</span>
      </td>
      <td>{listCellText(channels)}</td>
      <td>{cellText(relationship)}</td>
      <td>{cellText(decisionAuthority)}</td>
      <td>{cellText(frequency)}</td>
    </tr>
  );
};

const DetailTable = ({ stakeholders }: ExternalStakeholdersProps) => (
  <table className='external-stakeholders-table detail'>
    <thead>
      <tr>
        <th className='col-stakeholder'>Stakeholder</th>
        <th className='col-dos'>Dos</th>
        <th className='col-donts'>Don&apos;ts</th>
        <th className='col-comms'>Comms preferences</th>
        <th className='col-news'>News / Notes</th>
      </tr>
    </thead>
    <tbody>
      {stakeholders.map(stakeholder => (
        <DetailRow key={stakeholder.email} stakeholder={stakeholder} />
      ))}
    </tbody>
  </table>
);

const DetailRow = ({ stakeholder }: StakeholderRowProps) => {
  const { name, dos, donts, commsPreferences, news } = stakeholder;

  return (
    <tr>
      <td>
        <span className='external-stakeholders-name'>{name}</span>
      </td>
      <td>
        <GuidanceList items={dos} marker='✓' />
      </td>
      <td>
        <GuidanceList items={donts} marker='✕' />
      </td>
      <td>{cellText(commsPreferences)}</td>
      <td className='external-stakeholders-news'>{cellText(news)}</td>
    </tr>
  );
};

interface GuidanceListProps {
  items: string[];
  marker: string;
}

const GuidanceList = ({ items, marker }: GuidanceListProps) => {
  const present = presentItems(items);
  if (present.length === 0) return EMPTY_CELL;

  return (
    <>
      {present.map(item => (
        <span key={item} className='external-stakeholders-guidance'>
          {marker} {item}
        </span>
      ))}
    </>
  );
};
